import React, { useEffect, useState, useCallback } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';
import { useTenantAuth } from '../context/TenantAuthContext';

const TYPES = ['string', 'number', 'boolean', 'json'];
const STATUSES = ['active', 'inactive'];

export default function TenantSystemSettingFormPage() {
  const { settingId } = useParams();
  const isEdit = Boolean(settingId);
  const navigate = useNavigate();
  const { ready, user, hasPermission } = useTenantAuth();

  const [settingGroup, setSettingGroup] = useState('');
  const [settingKey, setSettingKey] = useState('');
  const [settingLabel, setSettingLabel] = useState('');
  const [settingType, setSettingType] = useState('string');
  const [description, setDescription] = useState('');
  const [isSensitive, setIsSensitive] = useState(false);
  const [status, setStatus] = useState('active');
  const [valueStr, setValueStr] = useState('');
  const [valueBool, setValueBool] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready && !hasPermission('tenant.system_settings.manage')) {
      navigate('/tenant/system-settings', { replace: true });
    }
  }, [ready, hasPermission, navigate]);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch(`/api/tenant/system-settings/${settingId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const e = json.entry;
      setSettingGroup(e.setting_group || '');
      setSettingKey(e.setting_key || '');
      setSettingLabel(e.setting_label || '');
      setSettingType(e.setting_type || 'string');
      setDescription(e.description || '');
      setIsSensitive(Boolean(e.is_sensitive));
      setStatus(e.status || 'active');
      if (e.setting_type === 'boolean') {
        setValueBool(Boolean(e.setting_value));
        setValueStr('');
      } else if (e.setting_type === 'json') {
        setValueStr(
          e.setting_value !== null && e.setting_value !== undefined
            ? JSON.stringify(e.setting_value, null, 2)
            : ''
        );
      } else {
        setValueStr(e.setting_value != null ? String(e.setting_value) : '');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, settingId]);

  useEffect(() => {
    load();
  }, [load]);

  const buildValuePayload = () => {
    if (settingType === 'boolean') return valueBool;
    if (settingType === 'number') {
      const n = Number(valueStr.trim());
      return Number.isFinite(n) ? n : valueStr;
    }
    if (settingType === 'json') {
      const t = valueStr.trim();
      if (!t) return null;
      try {
        return JSON.parse(t);
      } catch (_e) {
        return valueStr;
      }
    }
    return valueStr;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      setting_label: settingLabel.trim(),
      setting_type: settingType,
      description: description.trim() || null,
      is_sensitive: isSensitive,
      status,
      setting_value: buildValuePayload()
    };
    if (!isEdit) {
      body.setting_group = settingGroup.trim();
      body.setting_key = settingKey.trim();
    }
    try {
      const url = isEdit ? `/api/tenant/system-settings/${settingId}` : '/api/tenant/system-settings';
      const res = await tenantApiFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        body
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      navigate(`/tenant/system-settings/${json.entry.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready || !user) return null;

  if (!hasPermission('tenant.system_settings.manage')) {
    return <Navigate to="/tenant/system-settings" replace />;
  }

  if (isEdit && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit tenant setting' : 'New tenant setting'}
      </Typography>
      <Button component={Link} to={isEdit ? `/tenant/system-settings/${settingId}` : '/tenant/system-settings'} sx={{ mb: 2 }}>
        Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Box component="form" onSubmit={submit}>
          {!isEdit && (
            <>
              <TextField
                fullWidth
                required
                label="Setting group"
                value={settingGroup}
                onChange={(e) => setSettingGroup(e.target.value)}
                margin="normal"
                inputProps={{ maxLength: 64 }}
              />
              <TextField
                fullWidth
                required
                label="Setting key"
                value={settingKey}
                onChange={(e) => setSettingKey(e.target.value)}
                margin="normal"
                inputProps={{ maxLength: 128 }}
              />
            </>
          )}
          <TextField
            fullWidth
            required
            label="Label"
            value={settingLabel}
            onChange={(e) => setSettingLabel(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 512 }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={settingType} onChange={(e) => setSettingType(e.target.value)}>
              {TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={isSensitive} onChange={(e) => setIsSensitive(e.target.checked)} />}
            label="Sensitive (masked for users with view-only access)"
            sx={{ mt: 1, display: 'block' }}
          />

          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Value
          </Typography>
          {settingType === 'boolean' ? (
            <FormControlLabel
              control={<Switch checked={valueBool} onChange={(e) => setValueBool(e.target.checked)} />}
              label="Enabled / true"
            />
          ) : (
            <TextField
              fullWidth
              multiline={settingType === 'json' || settingType === 'string'}
              minRows={settingType === 'json' ? 6 : settingType === 'string' ? 3 : 1}
              type={settingType === 'number' ? 'number' : 'text'}
              value={valueStr}
              onChange={(e) => setValueStr(e.target.value)}
              margin="normal"
              sx={settingType === 'json' ? { fontFamily: 'monospace', fontSize: '0.85rem' } : undefined}
            />
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              component={Link}
              to={isEdit ? `/tenant/system-settings/${settingId}` : '/tenant/system-settings'}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
